import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { PaygapMetricItemDto } from "@green0meter/api-client";

interface PayDifferenceData {
  genderCoefficient: number;
  isSignificant: boolean;
}

interface PayDifferenceDataMultiple {
  classification1: PayDifferenceData | null;
  classification2: PayDifferenceData | null;
  classification3: PayDifferenceData | null;
}

interface GradePayGapData {
  gradeType: string; // "I", "II", "III"
  gradeId: string;
  salaryType: "net" | "basic" | "variable"; // salary component
  menAverage: number;
  womenAverage: number;
  payGap: number;
}

interface IncomeQuartileData {
  metric: string;
  men: number;
  women: number;
  czComparison?: number;
  isCurrency?: boolean;
}

interface SalaryCostData {
  men: {
    totalCost: number;
    sharePercent: number;
  };
  women: {
    totalCost: number;
    sharePercent: number;
  };
  total: {
    totalCost: number;
    sharePercent: number;
  };
}

interface EmployeeDistributionData {
  gender: string;
  totalHeadcount: number;
  monthlyContracts: number;
  hourlyContracts: number;
}

interface AnalysisSummary {
  employeeRecords: number;
  analysisDate: string;
  gapDetected: boolean;
  complianceRisk: "High" | "Medium" | "Low";
  // Statistical indicators
  discriminationCoefficient: number;
  standardErrorCoefficient: number;
  totalDataRecords: number;
  employeesAnalysisContracts: number;
  notInAnalysis: number;
  employeesAnalysisValid: number;
  women: number;
  men: number;
  excludedTraining: number;
  zeroIncomeRecords: number;
  invalidIncompleteRecords: number;
  degreesOfFreedom: number;
  testHypothesis: string;
  criticalValue: number;
  testStatistic: number;
  statisticalSignificance: boolean;
}

interface SeniorityPayGapData {
  position: string;
  menAverage: number;
  womenAverage: number;
  payGap: number;
  description: string;
  employeeCount: number;
}

interface PositionRolePayGapData {
  position: string;
  menAverage: number;
  womenAverage: number;
  payGap: number;
  seniorityLevel: number;
}

interface BlinderOaxacaData {
  grade: string;
  unadjustedGap: number;
  explainedByTraits: number;
  unexplainedByTraits: number;
}

interface ProcessedAnalysisData {
  payDifferenceData: PayDifferenceData;
  payDifferenceDataMultiple: PayDifferenceDataMultiple;
  unadjustedPayGap: number;
  incomeQuartileData: IncomeQuartileData[];
  salaryCostData: SalaryCostData;
  employeeDistributionData: EmployeeDistributionData[];
  analysisSummary: AnalysisSummary;
  seniorityPayGapData: SeniorityPayGapData[];
  positionRolePayGapData: PositionRolePayGapData[];
  gradePayGapData: GradePayGapData[];
  blinderOaxacaData: BlinderOaxacaData[];
}

export const usePaygapAnalysisData = (
  analysisResults?: PaygapMetricItemDto[] | null,
): ProcessedAnalysisData | null => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (!analysisResults || analysisResults.length === 0) return null;

    const availableKeys = analysisResults.map((v) => v.item);

    // Helper function to get value from analysis results
    const getValue = (key: string): string => {
      const item = analysisResults.find((v) => v.item === key);
      const value = item?.value || "0";

      return value;
    };

    // Helper function to parse numeric values
    const parseValue = (value: string): number => {
      if (
        value === "#DIV/0!" ||
        value === "#VALUE!" ||
        value === "#N/A" ||
        !value ||
        value === "null"
      )
        return 0;
      const parsed = parseFloat(value);

      return isNaN(parsed) ? 0 : parsed;
    };

    // 1. Pay Difference Data
    const genderPayGapValue = getValue("gender-pay-gap");
    const genderPayGap = parseValue(genderPayGapValue) * 100;
    const unadjustedPayGap = parseValue(getValue("difference-average-income")) * 100;
    const payDifferenceData: PayDifferenceData = {
      genderCoefficient: genderPayGap,
      isSignificant: Math.abs(genderPayGap) > 5,
    };

    // Multiple pay gap classifications
    const genderPayGap1Value = getValue("gender-pay-gap-1");
    const genderPayGap1 = parseValue(genderPayGap1Value) * 100;
    const genderPayGap2Value = getValue("gender-pay-gap-2");
    const genderPayGap2 = parseValue(genderPayGap2Value) * 100;

    const payDifferenceDataMultiple: PayDifferenceDataMultiple = {
      classification1: {
        genderCoefficient: genderPayGap,
        isSignificant: Math.abs(genderPayGap) > 5,
      },
      classification2:
        genderPayGap1Value && genderPayGap1Value !== "0"
          ? {
              genderCoefficient: genderPayGap1,
              isSignificant: Math.abs(genderPayGap1) > 5,
            }
          : null,
      classification3:
        genderPayGap2Value && genderPayGap2Value !== "0"
          ? {
              genderCoefficient: genderPayGap2,
              isSignificant: Math.abs(genderPayGap2) > 5,
            }
          : null,
    };

    // 2. Income Quartile Data
    const avgMaleValue = getValue("quartile-average-male");
    const avgFemaleValue = getValue("quartile-average-female");

    const incomeQuartileData: IncomeQuartileData[] = [
      {
        metric: t("paygap.data.quartile.average"),
        men: parseValue(avgMaleValue),
        women: parseValue(avgFemaleValue),
        czComparison: parseValue(getValue("comparison-cz-average-income")),
        isCurrency: true,
      },
      {
        metric: t("paygap.data.quartile.q1"),
        men: parseValue(getValue("quartile-first-male")),
        women: parseValue(getValue("quartile-first-female")),
        czComparison: parseValue(getValue("comparison-cz-first-quartile")),
        isCurrency: true,
      },
      {
        metric: t("paygap.data.quartile.q2"),
        men: parseValue(getValue("quartile-second-male")),
        women: parseValue(getValue("quartile-second-female")),
        czComparison: parseValue(getValue("comparison-cz-second-quartile-median")),
        isCurrency: true,
      },
      {
        metric: t("paygap.data.quartile.q3"),
        men: parseValue(getValue("quartile-third-male")),
        women: parseValue(getValue("quartile-third-female")),
        czComparison: parseValue(getValue("comparison-cz-third-quartile")),
        isCurrency: true,
      },
      {
        metric: t("paygap.data.quartile.q4"),
        men: parseValue(getValue("quartile-fourth-male")),
        women: parseValue(getValue("quartile-fourth-female")),
        czComparison: parseValue(getValue("comparison-cz-fourth-quartile-maximum")),
        isCurrency: true,
      },
      {
        metric: t("paygap.data.quartile.minimum"),
        men: parseValue(getValue("quartile-minimum-male")),
        women: parseValue(getValue("quartile-minimum-female")),
        isCurrency: false,
      },
      {
        metric: t("paygap.data.quartile.range"),
        men: parseValue(getValue("quartile-range-male")),
        women: parseValue(getValue("quartile-range-female")),
        isCurrency: false,
      },
    ];

    // 3. Total Salary Cost (Analyzed Employees)
    const menCost = parseValue(getValue("total-cost-male"));
    const womenCost = parseValue(getValue("total-cost-female"));
    const totalCost = menCost + womenCost;
    const salaryCostData: SalaryCostData = {
      men: {
        totalCost: menCost,
        sharePercent: totalCost > 0 ? (menCost / totalCost) * 100 : 0,
      },
      women: {
        totalCost: womenCost,
        sharePercent: totalCost > 0 ? (womenCost / totalCost) * 100 : 0,
      },
      total: {
        totalCost: totalCost,
        sharePercent: 100,
      },
    };

    // 4. Employee Distribution
    const menCountValue = getValue("distribution-male");
    const womenCountValue = getValue("distribution-female");

    const menCount = parseValue(menCountValue);
    const womenCount = parseValue(womenCountValue);
    const totalCount = menCount + womenCount;
    const employeeDistributionData: EmployeeDistributionData[] = [
      {
        gender: t("paygap.data.gender.men"),
        totalHeadcount: menCount,
        monthlyContracts: parseValue(getValue("distribution-male-monthly")),
        hourlyContracts: parseValue(getValue("distribution-male-hourly")),
      },
      {
        gender: t("paygap.data.gender.women"),
        totalHeadcount: womenCount,
        monthlyContracts: parseValue(getValue("distribution-female-monthly")),
        hourlyContracts: parseValue(getValue("distribution-female-hourly")),
      },
      {
        gender: t("paygap.data.gender.total"),
        totalHeadcount: totalCount,
        monthlyContracts:
          parseValue(getValue("distribution-male-monthly")) +
          parseValue(getValue("distribution-female-monthly")),
        hourlyContracts:
          parseValue(getValue("distribution-male-hourly")) +
          parseValue(getValue("distribution-female-hourly")),
      },
    ];

    // 5. Seniority Pay Gap Data
    const seniorityPayGapData: SeniorityPayGapData[] = [
      {
        position: "5",
        menAverage: 0,
        // These will be populated from real data when available
        womenAverage: 0,
        payGap: parseValue(getValue("seniority-5-difference")) * 100,
        description: t("paygap.data.seniority.level5"),
        employeeCount: 0,
      },
      {
        position: "4",
        menAverage: 0,
        womenAverage: 0,
        payGap: parseValue(getValue("seniority-4-difference")) * 100,
        description: t("paygap.data.seniority.level4"),
        employeeCount: 0,
      },
      {
        position: "3",
        menAverage: 0,
        womenAverage: 0,
        payGap: parseValue(getValue("seniority-3-difference")) * 100,
        description: t("paygap.data.seniority.level3"),
        employeeCount: 0,
      },
      {
        position: "2",
        menAverage: 0,
        womenAverage: 0,
        payGap: parseValue(getValue("seniority-2-difference")) * 100,
        description: t("paygap.data.seniority.level2"),
        employeeCount: 0,
      },
      {
        position: "1",
        menAverage: 0,
        womenAverage: 0,
        payGap: parseValue(getValue("seniority-1-difference")) * 100,
        description: t("paygap.data.seniority.level1"),
        employeeCount: 0,
      },
    ];

    // 6. Position-based Pay Gap Data (by job position metrics)
    // Build everything dynamically from response keys/values
    const toTitleCase = (s: string): string => {
      // Words that should remain lowercase in titles
      const lowercaseWords = [
        "a",
        "an",
        "and",
        "as",
        "at",
        "but",
        "by",
        "for",
        "if",
        "in",
        "of",
        "on",
        "or",
        "the",
        "to",
        "up",
        "yet",
        "a",
        "an",
        "ale",
        "až",
        "bez",
        "do",
        "i",
        "k",
        "ke",
        "na",
        "nad",
        "nebo",
        "od",
        "po",
        "před",
        "pro",
        "při",
        "se",
        "si",
        "u",
        "v",
        "ve",
        "za",
        "ze",
        "že",
      ];

      return s
        .replace(/-/g, " ")
        .split(" ")
        .map((word, index) => {
          const lowerWord = word.toLowerCase();

          // Always capitalize first word, or if word is not in lowercase list
          if (index === 0 || !lowercaseWords.includes(lowerWord)) {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          }

          return lowerWord;
        })
        .join(" ");
    };

    // Parse keys like: position-<slug>(-<seniorityId>)(-male|-female)?
    const parsePositionKey = (
      key: string,
    ): { baseSlug: string; seniorityId?: number } | null => {
      if (!key.startsWith("position-")) return null;
      let body = key.slice("position-".length);

      body = body.replace(/-(male|female)$/i, "");

      const m = body.match(/^(.*?)-(\d+)$/);

      if (m) return { baseSlug: m[1], seniorityId: Number(m[2]) };

      return { baseSlug: body };
    };

    const titlesBySlug: Record<string, string> = {};
    const seniorityLevelBySlug: Record<string, number> = {};
    const discoveredSlugs = new Set<string>();

    analysisResults.forEach((v) => {
      const parsed = parsePositionKey(v.item);

      if (!parsed) return;

      const { baseSlug, seniorityId } = parsed;

      discoveredSlugs.add(baseSlug);

      const val = String(v.value ?? "").trim();

      // If the value contains letters, assume it's a human-readable title and store it
      if (/[A-Za-z\p{L}]/u.test(val)) {
        titlesBySlug[baseSlug] = val;
      }
      if (typeof seniorityId === "number" && !Number.isNaN(seniorityId)) {
        seniorityLevelBySlug[baseSlug] = seniorityId;
      }
    });

    const positionBaseSlugs: string[] = Array.from(discoveredSlugs);

    // Find keys for averages; support optional trailing -<id>
    const findGenderKey = (slug: string, gender: "male" | "female"): string => {
      const regex = new RegExp(`^position-${slug}(?:-\\d+)?-${gender}$`);

      const found = availableKeys.find((k) => regex.test(k));

      return found || `position-${slug}-${gender}`;
    };

    const positionRolePayGapData: PositionRolePayGapData[] = positionBaseSlugs.map(
      (slug) => {
        const maleKey = findGenderKey(slug, "male");
        const femaleKey = findGenderKey(slug, "female");

        const menAverage = parseValue(getValue(maleKey));
        const womenAverage = parseValue(getValue(femaleKey));

        let payGap = 0;

        if (menAverage > 0 && womenAverage >= 0) {
          // Pay gap as percentage relative to men (positive => men higher)
          payGap = ((menAverage - womenAverage) / menAverage) * 100;
        } else {
          // Backward compatibility: if legacy single key exists (with or without id), use it
          const legacyRegex = new RegExp(`^position-${slug}(?:-\\d+)?$`);
          const legacyKey =
            availableKeys.find((k) => legacyRegex.test(k)) || `position-${slug}`;
          const legacyVal = parseValue(getValue(legacyKey));

          if (legacyVal !== 0) {
            payGap = legacyVal * 100;
          }
        }

        return {
          position: titlesBySlug[slug] || toTitleCase(slug),
          menAverage,
          womenAverage,
          payGap,
          seniorityLevel: seniorityLevelBySlug[slug] ?? 0,
        };
      },
    );

    // Grade-based pay gap data
    // Keys: "grade-gap-I-1" (net), "grade-gap-I-basic-1" (basic), "grade-gap-I-variable-1" (variable)
    const gradePayGapData: GradePayGapData[] = [];
    const gradeGapBasicRegex = /^grade-gap-([IVX]+)-basic-?(.+)$/;
    const gradeGapVariableRegex = /^grade-gap-([IVX]+)-variable-?(.+)$/;
    const gradeGapNetRegex = /^grade-gap-([IVX]+)-(.+)$/;

    const gradeKeys = availableKeys.filter((k) => k.startsWith("grade-gap-"));

    gradeKeys.forEach((key) => {
      let gradeType: string;
      let gradeId: string;
      let salaryType: "net" | "basic" | "variable";

      const basicMatch = key.match(gradeGapBasicRegex);
      const variableMatch = key.match(gradeGapVariableRegex);

      if (basicMatch) {
        [, gradeType, gradeId] = basicMatch;
        salaryType = "basic";
      } else if (variableMatch) {
        [, gradeType, gradeId] = variableMatch;
        salaryType = "variable";
      } else {
        const netMatch = key.match(gradeGapNetRegex);

        if (!netMatch) return;
        [, gradeType, gradeId] = netMatch;
        // Skip if gradeId starts with "basic" or "variable" (already handled above)
        if (gradeId.startsWith("basic") || gradeId.startsWith("variable")) return;
        salaryType = "net";
      }

      const value = parseValue(getValue(key));
      const payGap = value * 100;

      gradePayGapData.push({
        gradeType,
        gradeId,
        salaryType,
        menAverage: 0,
        womenAverage: 0,
        payGap,
      });
    });

    // Sort by gradeType, then salaryType, then gradeId
    const salaryTypeOrder: Record<string, number> = { net: 0, basic: 1, variable: 2 };

    gradePayGapData.sort((a, b) => {
      if (a.gradeType !== b.gradeType) {
        return a.gradeType.localeCompare(b.gradeType);
      }
      if (a.salaryType !== b.salaryType) {
        return (salaryTypeOrder[a.salaryType] ?? 0) - (salaryTypeOrder[b.salaryType] ?? 0);
      }

return a.gradeId.localeCompare(b.gradeId, undefined, { numeric: true });
    });

    // 7. Analysis Summary
    const analysisSummary: AnalysisSummary = {
      // Deduct 1 from total count, but keep at least 1 record
      employeeRecords: Math.max(1, totalCount - 1),
      analysisDate: new Date().toLocaleDateString(),
      gapDetected: genderPayGap !== 0,
      complianceRisk:
        Math.abs(genderPayGap) > 10
          ? "High"
          : Math.abs(genderPayGap) > 5
            ? "Medium"
            : "Low",
      // Statistical indicators from analysis results
      discriminationCoefficient: parseValue(
        getValue("statistical-indicators-discrimination-coefficient"),
      ),
      standardErrorCoefficient: parseValue(
        getValue("statistical-indicators-standard-error-coefficient"),
      ),
      totalDataRecords: parseValue(
        getValue("statistical-indicators-total-data-records"),
      ),
      employeesAnalysisContracts: parseValue(
        getValue("statistical-indicators-employees-analysis-contracts"),
      ),
      notInAnalysis: parseValue(getValue("statistical-indicators-not-in-analysis")),
      employeesAnalysisValid: parseValue(
        getValue("statistical-indicators-employees-analysis-valid"),
      ),
      women: parseValue(getValue("statistical-indicators-women")),
      men: parseValue(getValue("statistical-indicators-men")),
      excludedTraining: parseValue(
        getValue("statistical-indicators-excluded-training"),
      ),
      zeroIncomeRecords: parseValue(
        getValue("statistical-indicators-zero-income-records"),
      ),
      invalidIncompleteRecords: parseValue(
        getValue("statistical-indicators-invalid-incomplete-records"),
      ),
      degreesOfFreedom: parseValue(
        getValue("statistical-indicators-degrees-of-freedom"),
      ),
      testHypothesis:
        getValue("test-diff-from-zero-h0-0-h1-not-0") ||
        "H₀: wage/salary difference = 0%; H₁: wage/salary difference ≠ 0",
      criticalValue: parseValue(
        getValue("test-diff-from-zero-critical-value-t-two-tailed"),
      ),
      testStatistic: parseValue(getValue("test-diff-from-zero-test-statistic-t")),
      statisticalSignificance:
        parseValue(getValue("test-diff-from-zero-statistical-significance")) === 1,
    };

    // 8. Blinder-Oaxaca Decomposition Data
    const blinderOaxacaGrades = ["grade-1", "grade-2", "grade-3"];
    const blinderOaxacaData: BlinderOaxacaData[] = blinderOaxacaGrades
      .map((grade) => ({
        grade,
        unadjustedGap:
          parseValue(getValue(`blinder-oaxaca-unadjusted-gap-${grade}`)) * 100,
        explainedByTraits:
          parseValue(getValue(`blinder-oaxaca-explained-by-traits-in-regression-${grade}`)) * 100,
        unexplainedByTraits:
          parseValue(getValue(`blinder-oaxaca-unexplained-by-traits-in-regression-${grade}`)) * 100,
      }))
      .filter((d) => d.unadjustedGap !== 0 || d.explainedByTraits !== 0 || d.unexplainedByTraits !== 0);

    return {
      payDifferenceData,
      payDifferenceDataMultiple,
      unadjustedPayGap,
      incomeQuartileData,
      salaryCostData,
      employeeDistributionData,
      analysisSummary,
      seniorityPayGapData,
      positionRolePayGapData,
      gradePayGapData,
      blinderOaxacaData,
    };
  }, [analysisResults, t]);
};

export type {
  PayDifferenceData,
  PayDifferenceDataMultiple,
  IncomeQuartileData,
  SalaryCostData,
  EmployeeDistributionData,
  AnalysisSummary,
  SeniorityPayGapData,
  PositionRolePayGapData,
  GradePayGapData,
  BlinderOaxacaData,
  ProcessedAnalysisData,
};
